import type { PagedResponse } from '../../types/pagedResponse'
import type {
  PurchaseRequestDetails,
  PurchaseRequestListItem,
  PurchaseRequestListPage,
  SendPurchaseRequestToPaymentResult
} from '../contracts'
import { requestJson } from '../httpClient'
import { CreatePurchaseRequestContract, PURCHASE_REQUESTS_API_PREFIX } from './shared'

const PURCHASE_REQUESTS_STATUS_ERROR_MESSAGES = {
  403: 'Недостаточно прав для просмотра заявок.'
} as const

export function createPurchaseRequest(
  apiBaseUrl: string,
  payload: CreatePurchaseRequestContract
): Promise<PurchaseRequestDetails> {
  return requestJson<PurchaseRequestDetails>(`${apiBaseUrl}${PURCHASE_REQUESTS_API_PREFIX}`, {
    method: 'POST',
    body: payload,
    fallbackErrorMessage: 'Не удалось создать заявку.'
  })
}

export function getPurchaseRequests(
  apiBaseUrl: string,
  params: {
    page: number
    pageSize: number
    requesterUserId?: number
    assigneeUserId?: number
    unassignedOnly?: boolean
    statuses?: string[]
    displayStatuses?: string[]
    paymentProgress?: string
    fulfillmentProgress?: string
    itemName?: string
    productId?: number
    supplierId?: number
    categoryId?: number
    projectId?: number
    userId?: number
    deletedOnly?: boolean
    unreadCommentsOnly?: boolean
  }
): Promise<PagedResponse<PurchaseRequestListItem>> {
  const query = new URLSearchParams({ page: String(params.page), pageSize: String(params.pageSize) })

  if (Number.isInteger(params.userId) && (params.userId || 0) > 0) {
    query.set('userId', String(params.userId))
  }
  if (Number.isInteger(params.requesterUserId) && (params.requesterUserId || 0) > 0) {
    query.set('requesterUserId', String(params.requesterUserId))
  }
  if (Number.isInteger(params.assigneeUserId) && (params.assigneeUserId || 0) > 0) {
    query.set('assigneeUserId', String(params.assigneeUserId))
  }
  if (params.unassignedOnly) {
    query.set('unassignedOnly', 'true')
  }

  for (const status of params.statuses ?? []) {
    const normalizedStatus = status.trim()
    if (normalizedStatus) {
      query.append('status', normalizedStatus)
    }
  }
  for (const displayStatus of params.displayStatuses ?? []) {
    const normalizedStatus = displayStatus.trim()
    if (normalizedStatus) {
      query.append('displayStatus', normalizedStatus)
    }
  }

  const normalizedPaymentProgress = params.paymentProgress?.trim() ?? ''
  if (normalizedPaymentProgress) {
    query.set('paymentProgress', normalizedPaymentProgress)
  }
  const normalizedFulfillmentProgress = params.fulfillmentProgress?.trim() ?? ''
  if (normalizedFulfillmentProgress) {
    query.set('fulfillmentProgress', normalizedFulfillmentProgress)
  }

  const normalizedItemName = params.itemName?.trim() ?? ''
  if (normalizedItemName) {
    query.set('itemName', normalizedItemName)
  }

  if (Number.isInteger(params.productId) && (params.productId ?? 0) > 0) {
    query.set('productId', String(params.productId))
  }
  if (Number.isInteger(params.supplierId) && (params.supplierId ?? 0) > 0) {
    query.set('supplierId', String(params.supplierId))
  }
  if (Number.isInteger(params.categoryId) && (params.categoryId ?? 0) > 0) {
    query.set('categoryId', String(params.categoryId))
  }
  if (Number.isInteger(params.projectId) && (params.projectId ?? 0) > 0) {
    query.set('projectId', String(params.projectId))
  }
  if (params.deletedOnly) {
    query.set('deleted', 'only')
  }
  if (params.unreadCommentsOnly) {
    query.set('comments', 'unread')
  }

  return requestJson<PurchaseRequestListPage>(`${apiBaseUrl}${PURCHASE_REQUESTS_API_PREFIX}?${query.toString()}`, {
    fallbackErrorMessage: 'Не удалось загрузить список заявок.',
    statusErrorMessages: PURCHASE_REQUESTS_STATUS_ERROR_MESSAGES
  }).then((response) => response as PagedResponse<PurchaseRequestListItem>)
}

export function getPurchaseRequestById(
  apiBaseUrl: string,
  requestId: number,
  signal?: AbortSignal
): Promise<PurchaseRequestDetails> {
  return requestJson<PurchaseRequestDetails>(`${apiBaseUrl}${PURCHASE_REQUESTS_API_PREFIX}/${requestId}`, {
    signal,
    fallbackErrorMessage: 'Не удалось загрузить детали заявки.',
    statusErrorMessages: PURCHASE_REQUESTS_STATUS_ERROR_MESSAGES
  })
}

export function updatePurchaseRequest(
  apiBaseUrl: string,
  requestId: number,
  payload: CreatePurchaseRequestContract
): Promise<PurchaseRequestDetails> {
  return requestJson<PurchaseRequestDetails>(`${apiBaseUrl}${PURCHASE_REQUESTS_API_PREFIX}/${requestId}`, {
    method: 'PUT',
    body: payload,
    fallbackErrorMessage: 'Не удалось обновить заявку.'
  })
}

export function sendPurchaseRequestToPayment(
  apiBaseUrl: string,
  requestId: number
): Promise<SendPurchaseRequestToPaymentResult> {
  return requestJson<SendPurchaseRequestToPaymentResult>(`${apiBaseUrl}${PURCHASE_REQUESTS_API_PREFIX}/${requestId}/send-to-payment`, {
    method: 'POST',
    fallbackErrorMessage: 'Не удалось отправить заявку в оплату.'
  })
}
